import React, { useState, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { CloseRounded, ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../components/ButtonComponent';
import CircularProgressLoader from '../components/CircularProgressLoader';
import { BUTTON_PRIMARY, BUTTON_WARNING, SUCCESS, ERROR, TOAST_ERROR } from '../assets/constants/Constants';
import { HelmetIcon, BackpackIcon, HandbagIcon, SuitcaseIcon, TrolleyIcon, RADIO_CHECKED_ICON, RADIO_UNCHECKED_ICON, SIGN_CHECKED_ICON, SIGN_UNCHECKED_ICON } from '../assets/constants/Icons';
import { LOCKER_SIZE_SELECTION, LOCKER_UNIT_SELECTION, OPEN_LOCKER } from '../assets/constants/PageList';
import { LOCAL_STORAGE, SESSION_STORAGE, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, SS_PAYMENT_GATEWAY, SS_PAYMENT_GATEWAY_ACCESS_KEY, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY, LS_PROP_LOCKER_UNIT_DETAILS } from '../assets/constants/BrowserStorageKeys';
import { getSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import { reserveLocker } from '../api/api';
import { prePaymentRegistration } from '../lib/BackendUtils';
import { getPathForNextPage, convertDurationStringToMilliseconds, verifyUserProximity } from '../lib/Utils';
import CustomDialog from '../components/CustomDialog';
import ShowToast from '../components/ToastComponent';

const LockerSizePage = () => {
    const history = useHistory();
    const {t} = useTranslation();
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading');
    const [showLockerSizesModal, setLockerSizesModalVisibility] = useState(false);
    const [showBookingConfirmationModal, setBookingConfirmationModalVisibility] = useState(false);
    const { lockerBankId, assignmentSlug, userId, availableLockerSizes, isManualLockerSelectionEnabled } = history.location?.state;
    const isPricingEnabled = tenantAndAssignmentSettings?.is_pricing;
    const [selectedSizeId, setSelectedSizeId] = useState(null);
    const [selectedSlotId, setSelectedSlotId] = useState(null);
    const [showBookingSlots, setShowBookingSlots] = useState(false);
    const [userProximityError, setUserProximityError] = useState('');
    const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);
    const [initializedSizeId, setInitializedSizeId] = useState(null);
    // const isManualLockerSelectionEnabled = history.location?.state?.isManualLockerSelectionEnabled;

    useEffect(() => {
        if (isPricingEnabled) {
            sortAndFormatSlotDuration();
        }

        setIsLoading(false);
    }, []);

    const sortAndFormatSlotDuration = () => {
        availableLockerSizes.forEach((lockerSize) => {
            lockerSize.dimensions = `${parseFloat(lockerSize.width)}\" x ${parseFloat(lockerSize.length)}\" x ${parseFloat(lockerSize.height)}\"`;

            lockerSize.size_pricing.forEach((slot) => {
                slot.durationInMilliseconds = convertDurationStringToMilliseconds(slot.duration);
            });

            const incrementallySortedBookingSlots = sortBookingSlotsBasedOnDuration(lockerSize.size_pricing);

            let previousDurationInMilliseconds = 0;
            incrementallySortedBookingSlots.forEach((slot) => {
                slot.durationRange = `${previousDurationInMilliseconds / 3600000} - ${slot.durationInMilliseconds / 3600000} Hours`;
                previousDurationInMilliseconds = slot.durationInMilliseconds;
            });

            lockerSize.bookingSlots = incrementallySortedBookingSlots;
        });
    };

    const sortBookingSlotsBasedOnDuration = (slotsArray) => {
        return slotsArray.slice().sort((a, b) => a.durationInMilliseconds - b.durationInMilliseconds);
    };

   const lockerSizeClickHandler = (item) => {
    if (isPricingEnabled) {
        const isSameSizeClicked = selectedSizeId === item.id;
        setSelectedSizeId(item.id); 
       // setSelectedSlotId(item.bookingSlots[0].id); 
        setShowBookingSlots(true);
        if (!isSameSizeClicked && item.bookingSlots?.length > 0) {
            setSelectedSlotId(item.bookingSlots[0].id);
            setInitializedSizeId(item.id);
        }
    } else {
        lockerSizeSelectionHandler(item);
    }
    };


    const lockerSizeSelectionHandler = async (item) => {
        if (isManualLockerSelectionEnabled) {
            history.replace({
                pathname: getPathForNextPage(LOCKER_SIZE_SELECTION, LOCKER_UNIT_SELECTION),
                state: {
                    lockerBankId,
                    assignmentSlug,
                    userId,
                    selectedLockerSize: item
                }
            });
        }
        else {
            setIsLoading(true);
            try {
                if (tenantAndAssignmentSettings?.is_geofencing) {
                    const lockerBankLatitude = lockerBankInfo?.latitude ? parseFloat(lockerBankInfo?.latitude) : null;
                    const lockerBankLongitude = lockerBankInfo?.longitude ? parseFloat(lockerBankInfo?.longitude) : null;
                    const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
                    const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);
                    // devConsoleLog('is user nearby locker bank: ', isUserNearbyLockerBank);
        
                    if ( !isUserNearbyLockerBank.isNearby ) {
                        setLoadingMessage('');
                        setIsLoading(false);
                        setUserProximityError(isUserNearbyLockerBank.message);
                        setIsUserProximityErrorModalVisible(true);
        
                        return;
                    }
                }

                const reserveLockerResponse = await reserveLocker(assignmentSlug, lockerBankId, userId, item.code);
                if (reserveLockerResponse.status === SUCCESS) {
                    const lockerUnitDetails = {
                        ref_id: reserveLockerResponse?.data?.transaction_id,
                        is_already_opened_once: false,
                        locker_bank: {
                            name: reserveLockerResponse?.data?.locker_bank_name
                        },
                        locker_door: {
                            door_no: reserveLockerResponse?.data?.door_no
                        }
                    };

                    history.replace({
                        pathname: getPathForNextPage(LOCKER_SIZE_SELECTION, OPEN_LOCKER),
                        state: {
                            lockerUnitDetails
                        }
                    });
                }
            } catch (error) {
                setIsLoading(false);
                console.error('Error while reserving locker: ', error);
            }
        }
    };

    const bookButtonHandler = async () => {
        setLoadingMessage('Initiating payment. Please wait.');
        setIsLoading(true);

        if (tenantAndAssignmentSettings?.is_geofencing) {
            const lockerBankLatitude = lockerBankInfo?.latitude ? parseFloat(lockerBankInfo?.latitude) : null;
            const lockerBankLongitude = lockerBankInfo?.longitude ? parseFloat(lockerBankInfo?.longitude) : null;
            const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);
            // devConsoleLog('is user nearby locker bank: ', isUserNearbyLockerBank);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        const selectedSize = availableLockerSizes.filter((lockerSize) => selectedSizeId === lockerSize?.id)[0];
        const selectedSlot = selectedSize?.size_pricing.filter((slot) => selectedSlotId === slot?.id)[0];

        const reserveLockerResponse = await reserveLockerTillSuccessfulPayment(selectedSize, selectedSlot);
        if (reserveLockerResponse?.status === SUCCESS) {
            const prePaymentResponse = await makePrePaymentRegistration(reserveLockerResponse.data);

            if (prePaymentResponse?.status === SUCCESS) {
                const lockerUnitDetails = {
                    ref_id: reserveLockerResponse?.data?.transaction_id,
                    is_already_opened_once: false,
                    locker_bank: {
                        name: reserveLockerResponse?.data?.locker_bank_name
                    },
                    locker_door: {
                        door_no: reserveLockerResponse?.data?.door_no
                    }
                };

                if (prePaymentResponse?.data?.data?.payment_gateway === 'stripe') {
                    lockerUnitDetails.payment_records = [
                        {
                            payment_gateway: prePaymentResponse?.data?.data?.payment_gateway,
                            payment_gateway_reference: prePaymentResponse?.data?.data?.payment_id,
                            transaction_id: prePaymentResponse?.data?.data?.transaction_id
                        }
                    ];
                    console.log('locker unit details: ', lockerUnitDetails);
                    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY, true);
                    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PROP_LOCKER_UNIT_DETAILS, lockerUnitDetails);

                    window.location.href = prePaymentResponse?.data?.data?.field_1;
                    return;
                }

                const lockerData = {
                    reservedLocker: reserveLockerResponse?.data,
                    locker_bank: {
                        name: reserveLockerResponse?.data?.locker_bank_name,
                        location: {
                            name: reserveLockerResponse?.data?.locker_bank_location
                        }
                    },
                    assignment: {
                        payment_gateway: [ reserveLockerResponse?.data?.assignment_payment_gateway ]
                    },
                    payment_records: [
                        { amount: prePaymentResponse?.data?.data?.amount }
                    ],
                    user: {
                        username: reserveLockerResponse?.data?.username ? reserveLockerResponse?.data?.username : "",
                        email: reserveLockerResponse?.data?.transaction_payments[0]?.user_email ? reserveLockerResponse?.data?.transaction_payments[0].user_email : "",
                        mobile_number: reserveLockerResponse?.data?.transaction_payments[0]?.user_mobile ? reserveLockerResponse?.data?.transaction_payments[0].user_mobile : ""
                    }
                };

                storeSecureItemInSpecifiedStorage(SESSION_STORAGE, SS_PAYMENT_GATEWAY, prePaymentResponse?.data?.data?.payment_gateway);
                storeSecureItemInSpecifiedStorage(SESSION_STORAGE, SS_PAYMENT_GATEWAY_ACCESS_KEY, prePaymentResponse?.data?.data?.payment_gateway_reference);

                setIsLoading(false);

                history.replace({
                    pathname: '/payment-details',
                    state: {
                        lockerUnitDetails,
                        isLockerReservedTillSuccessfulPayment: true,
                        lockerData,
                        currentReferenceId: prePaymentResponse?.data?.data?.transaction_id,
                    }
                });
            }
            else if (prePaymentResponse.status === ERROR) {
                ShowToast(t(prePaymentResponse.message), TOAST_ERROR);
            }
        }

        setIsLoading(false);
    };

    const reserveLockerTillSuccessfulPayment = async (selectedSize, selectedSlot) => {
        try {
            return await reserveLocker(assignmentSlug, lockerBankId, userId, selectedSize?.code, null, selectedSlot?.id, parseFloat(selectedSlot?.price));
        } catch (error) {
            console.error('Error while reserving locker before payment: ', error);
        }
    };

    const makePrePaymentRegistration = async (lockerData) => {
        try {
            const body = {
                "payment_reference_id": lockerData?.transaction_payments[0]?.transaction_id,
                "payment_gateway": lockerData?.transaction_payments[0]?.payment_gateway,
                "locker_bank_id": lockerBankId,
                "locker_door_number": lockerData?.door_no,
                "amount": lockerData?.transaction_payments[0]?.amount,
                "currency": lockerData?.transaction_payments[0]?.currency,
                "payment_mode": lockerData?.transaction_payments[0]?.payment_mode
            };

            if (lockerData?.transaction_payments[0]?.payment_gateway === 'stripe') {
                body.success_url = `${window.location.protocol}//${window.location.host}/swipe-to-open`;
                body.cancel_url = `${window.location.protocol}//${window.location.host}/home-screen`;
            }

            return await prePaymentRegistration(body);
        } catch (error) {
            console.error('Error while initating payment: ', error);
        }
    };

    const LockerSize = ({ lockerItem, index }) => {
        if (isPricingEnabled) {
            const showSlots = showBookingSlots && (selectedSizeId === lockerItem.id);

            return (
                <div className={`locker-size accordion ${showSlots ? 'expanded' : ''}`} key={index}>
                    <button className='accordion-header ' onClick={() => lockerSizeClickHandler(lockerItem)}>
                        <p className='size-name'>{lockerItem.name} Lockers</p>
                        <p className='units-available'>{lockerItem.available_doors_count} available</p>
                        <div className='icon-container'>
                            {selectedSizeId === lockerItem.id ? (
                                <ExpandLessRounded className='accordion-icon' />
                            ) : (
                                <ExpandMoreRounded className='accordion-icon' />
                            )}
                        </div>
                    </button>
                    {showSlots && (
                        <div className='booking-slot-table accordion-details'>
                            <div className='table-header-row'>
                                <p className='heading column-0'>Select</p>
                                <p className='heading column-1'>Slot</p>
                                <p className='heading column-2'>Price</p>
                            </div>
                            {lockerItem?.bookingSlots.map((bookingSlot, index) => (
                                <div
                                    key={index}
                                    className={`table-data-row booking-slot ${selectedSlotId === bookingSlot?.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedSlotId(bookingSlot?.id)}
                                >
                                    <div className='data column-0'>
                                        {selectedSlotId === bookingSlot?.id ? <SIGN_CHECKED_ICON /> : <SIGN_UNCHECKED_ICON />}
                                    </div>
                                    <p className='data column-1'>{bookingSlot?.durationRange}</p>
                                    <p className='data column-2'>{bookingSlot?.price}</p>
                                </div>
                            ))}
                            <div className={`locker-counter-container ${selectedSlotId ? 'active' : ''}`}>
                                <p className={`title ${selectedSlotId ? 'active' : ''}`}>{t('No. of Lockers')}</p>
                                <div className={`locker-counter ${selectedSlotId ? 'active' : ''}`}>
                                    <button className="counter-button decrement">−</button>
                                    <div className="locker-count">{selectedSlotId ? 1 : 0}</div>
                                    <button className="counter-button increment">+</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        else {
            return (
                <div className='locker-size accordion' key={index}>
                    <button className='accordion-header ' onClick={() => lockerSizeClickHandler(lockerItem)}>
                        <p className='size-name'>{lockerItem.name} Lockers</p>
                        <p className='units-available'>{lockerItem.available_doors_count} available</p>
                    </button>
                </div>
            );
        }
    }

    const renderLockerSizesModalContent = () => {
        return (
            <div className='modal-content'>
                <div className='title-and-close-button-container'>
                    <h2 className='title'>Locker Sizes</h2>
                    <button id='locker-sizes-modal-close-button' onClick={() => setLockerSizesModalVisibility(false)}>
                        <CloseRounded className='modal-close-icon'/>
                    </button>
                </div>
                <div className='sizewise-item-details'>
                    <div className='locker-items small-locker'>
                        <p className='locker-size'>Small Lockers: <span className='sizing-text'>Can fit items below</span></p>
                        <div className='item-details'>
                            <div className='item-icon'>
                                <HelmetIcon />
                            </div>
                            <div className='item-icon'>
                                <BackpackIcon />
                            </div>
                            <div className='item-icon'>
                                <HandbagIcon />
                            </div>
                        </div>
                    </div>
                    <div className='locker-items medium-locker'>
                        <p className='locker-size'>Medium Lockers: <span className='sizing-text'>Can fit items below</span></p>
                        <div className='item-details'>
                            <div className='item-icon'>
                                <HelmetIcon />
                            </div>
                            <div className='item-icon'>
                                <BackpackIcon />
                            </div>
                            <div className='item-icon'>
                                <SuitcaseIcon />
                            </div>
                        </div>
                    </div>
                    <div className='locker-items large-locker'>
                        <p className='locker-size'>Large Lockers: <span className='sizing-text'>Can fit items below</span></p>
                        <div className='item-details'>
                            <div className='item-icon'>
                                <HelmetIcon />
                            </div>
                            <div className='item-icon'>
                                <BackpackIcon />
                            </div>
                            <div className='item-icon'>
                                <SuitcaseIcon />
                            </div>
                            <div className='item-icon'>
                                <TrolleyIcon />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

 const renderBookingConfirmationModalContent = () => {
        const selectedSize = availableLockerSizes.filter((lockerSize) => selectedSizeId === lockerSize?.id)[0];
        const selectedSlot = selectedSize?.bookingSlots.filter((slot) => selectedSlotId === slot?.id)[0];
        const bookingStartDateAndTime = moment().format('DD MMM YYYY, hh:mm A');
        const bookingEndDateAndTime = moment().add(selectedSlot?.durationInMilliseconds, 'milliseconds').format('DD MMM YYYY, hh:mm A');

       return (
            <div className='modal-content'>
                <div className='booking-details'>
                    <div className='info-row'>
                        <p className='left-column'>Slot:</p>
                        <p className='right-column'>{selectedSlot?.durationRange}</p>
                    </div>
                    <div className='info-row'>
                        <p className='left-column'>{t('Start date & time:')}</p>
                        <div>
                            <p className='right-column'>{bookingStartDateAndTime.split(',')[1]}</p>
                            <p className='right-column'>{bookingStartDateAndTime.split(',')[0]}</p>
                        </div>
                    </div>
                    <div className='info-row'>
                        <p className='left-column'>{t('End date & time:')}</p>
                        <div>
                            <p className='right-column'>{bookingEndDateAndTime.split(',')[1]}</p>
                            <p className='right-column'>{bookingEndDateAndTime.split(',')[0]}</p>
                        </div>
                    </div>
                    <div className='info-row'>
                        <p className='left-column'>{`Price (${selectedSize?.currency}):`}</p>
                        <p className='right-column'>{selectedSlot?.price}</p>
                    </div>
                </div>
            </div>
       );
    
    };

    return (
        <div className='locker-size-page'>
            <div className='main-container'>
                {isLoading ? (
                    <div className='main'>
                        <CircularProgressLoader message={loadingMessage} />
                    </div>
                ) : (
                    <div className='main'>
                        <div className="instructions-and-locker-sizes-container first-container">
                            {isPricingEnabled && (
                                <div className='booking-instructions'>
                                    <h2 className='heading'>{t('Instructions')}</h2>
                                    <ul className='instructions-list'>
                                        <li className='instruction'>
                                            {t('Select a locker size that fits your luggage and the duration you need (hours or days).')}
                                            <button id='check-locker-sizes-button' onClick={() => setLockerSizesModalVisibility(true)}>{t('Check locker sizes')}</button>
                                        </li>
                                        <li className='instruction'>{t('Provide your details to confirm your booking.')}</li>     
                                        <li className='instruction'>{t('Pay for your booking to secure your locker.')}</li>   
                                    </ul>
                                </div>
                            )}
                            
                            <div className='locker-size-list'>
                                {availableLockerSizes.map((lockerItem, index) => (
                                    <LockerSize lockerItem={lockerItem} index={index} />
                                ))}
                            </div>
                        </div>

                        {isPricingEnabled && (    
                            <ButtonComponent
                                buttonId='book-button'
                                handleClick={() => setBookingConfirmationModalVisibility(true)}
                                disabled={(selectedSlotId ? false:true)}
                                title="Continue"
                                type={BUTTON_PRIMARY}
                            />
                        )}

                        <CustomDialog
                            dialogId='locker-sizes-modal'
                            dialogVisible={showLockerSizesModal}
                            onClose={() => setLockerSizesModalVisibility(false)}
                            children={renderLockerSizesModalContent}
                        />

                        <CustomDialog
                            dialogId='booking-confirmation-modal'
                            dialogVisible={showBookingConfirmationModal}
                            onClose={() => setBookingConfirmationModalVisibility(false)}
                            handleCancel={() => setBookingConfirmationModalVisibility(false)}
                            handleAccept={bookButtonHandler}
                            dialogTitle={t('Please Confirm Booking Details')}
                            children={renderBookingConfirmationModalContent}
                            buttonOneTitle={t('Cancel')}
                            buttonTwoTitle={t('Check-In')}
                            buttonProps={{
                                buttonOne: { type: BUTTON_WARNING },
                                buttonTwo: { type: BUTTON_PRIMARY }
                            }}
                        />
                        <CustomDialog
                            dialogId='user-proximity-error-modal'
                            dialogVisible={isUserProximityErrorModalVisible}
                            onClose={() => setIsUserProximityErrorModalVisible(false)}
                            handleCancel={() => setIsUserProximityErrorModalVisible(false)}
                            dialogTitle={t('Location Error')}
                            dialogContentText={userProximityError}
                            buttonOneTitle={t('Ok')}
                            buttonProps={{buttonOne: {type: BUTTON_PRIMARY}}}
                        />
                    </div>
                )}
            </div>
        </div>
    )}
export default LockerSizePage;